import React, { useEffect, useState } from "react";
import { capitalizeFirstLetter } from "../../../../utilz/function";
import Input from "../../../../components/input";
import Table from "../../../../components/table";
import { I_ProductData } from "../../types";
interface UserTableProps {
  selectedStatus?: string;
  handleStatusChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  data: I_ProductData[] | I_ProductData;
}

const VariantsTable: React.FC<UserTableProps> = ({ selectedStatus, data }) => {
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [hasVariants, setHasVariants] = useState(false);

  const columns = [
    { header: "SKU", accessor: "inventoryId" },
    { header: "Variant Name", accessor: "name" },
    { header: "Price", accessor: "price" },
    { header: "Quantity", accessor: "quantity" },
    { header: "Restock Level", accessor: "reorderLevel" },
  ];

  // Extract variants data from the product object
  const getVariantsData = (product: I_ProductData) => {
    if (
      !product.isVariant ||
      !product.variant ||
      product.variant.length === 0
    ) {
      // Return product as a single item if it has no variants
      return [
        {
          ...product,
          name: product.name,
          inventoryId: product.inventoryId,
          price: product.price || "N/A",
          quantity: product.quantity,
          status: product.approvalStatus,
          reorderLevel: product.reorderLevel,
        },
      ];
    }

    // Has variants
    return product.variant.map((variant) => ({
      ...product,
      name: variant.name,
      inventoryId: product.inventoryId,
      price: variant.price,
      quantity: variant.quantity,
      status: product.approvalStatus,
      reorderLevel: variant.reorderLevel,
    }));
  };

  useEffect(() => {
    // Handle both array and single object cases
    try {
      let processedData: I_ProductData[] = [];

      if (Array.isArray(data)) {
        processedData = data;
      } else if (data && typeof data === "object") {
        processedData = [data as I_ProductData];
      }

      // Check if there are any variants in the data
      const hasAnyVariants = processedData.some(
        (product) =>
          product.isVariant && product.variant && product.variant.length > 0
      );
      setHasVariants(hasAnyVariants);

      const allVariants = processedData.flatMap(getVariantsData);

      const filtered = allVariants.filter((item) => {
        const statusMatch = !selectedStatus || item?.status === selectedStatus;

        const searchMatch =
          !searchQuery ||
          item?.name?.toLowerCase().includes(searchQuery?.toLowerCase()) ||
          item?.category?.toLowerCase().includes(searchQuery?.toLowerCase());

        const typeMatch =
          !selectedType ||
          item?.status === selectedType ||
          item?.category === selectedType;

        return statusMatch && searchMatch && typeMatch;
      });

      setFilteredData(filtered);
    } catch (error) {
      console.error("Error processing product data:", error);
      setFilteredData([]);
      setHasVariants(false);
    }
  }, [data, selectedStatus, searchQuery, selectedType]);

  const combinedData = filteredData.map((item) => ({
    ...item,
    name: (
      <div>
        <div>{capitalizeFirstLetter(item?.name)}</div>
      </div>
    ),
    category: (
      <div>
        <div>{capitalizeFirstLetter(item?.category)}</div>
      </div>
    ),
    price: (
      <div>
        <div>₦{Number(item?.price).toLocaleString()}</div>
      </div>
    ),
    quantity: (
      <div>
        {item?.quantity} {item?.measurementUnit}
      </div>
    ),
    inventoryId: (
      <div>
        <div>{item?.inventoryId}</div>
      </div>
    ),
    reorderLevel: (
      <div>
        <div>{item?.reorderLevel} {item?.measurementUnit}</div>
      </div>
    ),
  }));

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  return (
    <div className="py-3 bg-white rounded-3xl shadow border w-full border-[#C9C9C9] pt-5">
      <div className="md:flex justify-between px-5">
        <p className="text-[20px] font-bold">Product Variants</p>
        <div className="md:flex gap-2 items-center md:mt-0 mt-2">
          <Input
            name="search"
            value={searchQuery}
            onChange={handleSearchChange}
            className="w-full"
            placeholder="Search variants"
            icon="searchIcon"
          />
        </div>
      </div>

      {hasVariants && combinedData?.length > 0 ? (
        <Table columns={columns} data={combinedData} selectedUserId={null} />
      ) : (
        <div className="opacity-80 mt-10 text-[14px] font-semibold text-center text-[#4E4E4E] mx-auto py-8">
          No variants found matching your criteria
        </div>
      )}
    </div>
  );
};

export default VariantsTable;
